import React, { useEffect, useState, useMemo } from "react";
import { useStore, useSelector } from "react-redux";
import { Card, Spinner } from "react-bootstrap";
import styles from "./ThreadedMessageViewer.module.css";
import { isSelfMessage, getMessageAttachments, resolveName } from "../utils/messageUtils";
import DoctorFemaleIcon from "../assets/Doctor_Female_96.png";
import DoctorMaleIcon from "../assets/Doctor_Male_96.png";
import ResourceViewerModal from "../modals/ResourceViewerModal";
import QuickResponse from "./QuickResponse";
import AttachmentErrorModal from "../modals/AttachmentErrorModal";
import ObservationCard from "../cards/ObservationCard";
import DiagnosticCard from "../cards/DiagnosticCard";
import MediaCard from "../cards/MediaCard";
import SpecimenCard from "../cards/SpecimenCard";
import DocumentReferenceCard from "../cards/DocumentReferenceCard";
import CarePlanCard from "../cards/CarePlanCard";
import ConditionCard from "../cards/ConditionCard";
import QuestionnaireResponseCard from "../cards/QuestionnaireResponseCard";

import { fetchAttachment } from "../utils/fetchAttachment";
import { getIconForContentType } from "../utils/getIconForContentType";
import { fetchObservationByReference } from "../services/observationService";
import { fetchDiagnosticReportByReference } from "../services/diagnosticReportService";
import { fetchDocumentReferenceByReference } from "../services/docReferenceService";
import { fetchMediaByReference } from "../services/mediaService";
import { fetchSpecimenByReference } from "../services/specimenService";
import { fetchCarePlanByReference } from "../services/carePlanService";
import { fetchConditionByReference } from "../services/conditionService";
import { fetchQuestionnaireResponseByReference } from "../services/questionnaireResponseService";

function ThreadMessageViewer({
    selectedComm,
    fhirClient,
    contextProvider,
    userSelection,
    fetchedPatient,
    fetchedProvider,
    fetchedEncounter,
    communicationService,
    setShowThanksModal,
    setShowReplyModal,
    setSelectedComm,
    selectMessageById
}) {
    const store = useStore();
    const byId = useSelector(state => state.messages.byId);
    const threadMap = useSelector(state => state.messages.threadMap);
    const providers = useSelector(state => state.providers);

    const [threadTitle, setThreadTitle] = useState("Conversation");
    const [nameMap, setNameMap] = useState({});
    const [loadingNames, setLoadingNames] = useState(true);

    //TODO get rid of these and use redux. 
    //dunno if we can get rid of these as we'd need to cache resources with potential attachments which wouldn't be so good.
    const [resolvedObservations, setResolvedObservations] = useState({});
    const [resolvedDiagnosticReports, setResolvedDiagnosticReports] = useState({});
    const [resolvedDocumentReferences, setResolvedDocumentReferences] = useState({});
    const [resolvedMediaReferences, setResolvedMediaReferences] = useState({});
    const [resolvedSpecimenReferences, setResolvedSpecimenReferences] = useState({});
    const [resolvedCarePlanReferences, setResolvedCarePlanReferences] = useState({});
    const [resolvedConditionReferences, setResolvedConditionReferences] = useState({});
    const [resolvedQuestionnaireResponseReferences, setResolvedQuestionnaireResponseReferences] = useState({});
    
    

    const rawAnchor = selectedComm?.partOf?.[0]?.reference;
    const fallbackAnchor = `Communication/${selectedComm?.id}`;
    const validAnchorRef = rawAnchor && rawAnchor !== "unknown" ? rawAnchor : fallbackAnchor;
    const anchorId = validAnchorRef?.split("/")[1];
    const threadIds = threadMap?.[anchorId] || [];

    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [showProviderModal, setShowProviderModal] = useState(false);
    const [providerModalDetails, setProviderModalDetails] = useState(null);
    const handleProviderModalClose = () => setShowProviderModal(false);

    const messages = useMemo(() => {
        // console.log("[ThreadMessageViewer] Messages to render:", threadIds.map(id => byId[id]).filter(Boolean));
        return threadIds
            .map(id => byId[id])
            .filter(Boolean)
            .sort((a, b) => new Date(b.sent) - new Date(a.sent));
    }, [threadIds, byId]);

    const referencesToResolve = useMemo(() => {
        const refs = new Set();
        messages.forEach(msg => {
            if (msg.sender?.reference) refs.add(msg.sender.reference);
            (msg.recipient || []).forEach(r => r?.reference && refs.add(r.reference));
            (msg.extension || []).forEach(ext =>
                (ext.extension || []).forEach(inner => {
                    const ref = inner?.valueReference?.reference;
                    if (ref) refs.add(ref);
                })
            );
        });
        //console.log("[ThreadMessageViewer] References to resolve:", Array.from(refs));
        return Array.from(refs);
    }, [messages]);

    const providerName = providerModalDetails?.name?.[0];
    const providerFullName = providerName
        ? `${providerName.prefix?.join(" ") || ""} ${providerName.given?.join(" ")} ${providerName.family || ""}`.trim()
        : "Provider";

    // Resolve names
    useEffect(() => {
        async function resolveAllNames() {
            setLoadingNames(true);
            const unresolvedRefs = referencesToResolve.filter(ref => !nameMap[ref]);
            if (unresolvedRefs.length === 0) {
                setLoadingNames(false);
                return;
            }

            try {
                const entries = await Promise.all(
                    unresolvedRefs.map(async ref => {
                        const name = await resolveName(ref, { getState: () => ({ providers }) }, fhirClient);
                        return [ref, name];
                    })
                );
                setNameMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
            } catch (err) {
                console.error("Error resolving names:", err);
            } finally {
                setLoadingNames(false);
            }
        }

        if (messages.length > 0) resolveAllNames();
        else setLoadingNames(false);
    }, [referencesToResolve, providers, fhirClient]);

    // Set thread title
    useEffect(() => {
        if (!loadingNames) {
            const participantsSet = new Set();
            for (const msg of messages) {
                const senderRef = msg.sender?.reference;
                if (senderRef) participantsSet.add(nameMap[senderRef] || senderRef);
                const recipients = msg.recipient;
                if (Array.isArray(recipients)) {
                    for (const r of recipients) {
                        const recipientRef = r?.reference;
                        if (recipientRef) participantsSet.add(nameMap[recipientRef] || recipientRef);
                    }
                }
            }
            setThreadTitle([...participantsSet].join(", ") || "Conversation");
        }
    }, [messages, nameMap, loadingNames]);


    // Fetch Observations and DiagnosticReports for attachments
    useEffect(() => {
        async function fetchReferencedResources() {
            for (const msg of messages) {
                const attachments = getMessageAttachments(msg);

                for (const att of attachments) {
                    // --- Observation ---
                    if (att.type === "reference" && att.url.startsWith("Observation/")) {
                        if (!resolvedObservations[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching Observation:", att.url);
                                //const obs = await fhirClient.request(att.url);
                                const obs = await fetchObservationByReference(att.url, fhirClient, store);
                                setResolvedObservations(prev => ({ ...prev, [att.url]: obs }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch Observation:", att.url, err);
                            }
                        }
                        continue;
                    }

                    // --- DiagnosticReport ---
                    if (att.type === "reference" && att.url.startsWith("DiagnosticReport/")) {
                        if (!resolvedDiagnosticReports[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching DiagnosticReport:", att.url);
                                //const dr = await fhirClient.request(att.url);
                                const dr = await fetchDiagnosticReportByReference(att.url, fhirClient, store);
                                setResolvedDiagnosticReports(prev => ({ ...prev, [att.url]: dr }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch DiagnosticReport:", att.url, err);
                            }
                        }
                        continue;
                    }

                    // --- DocumentReference ---
                    if (att.type === "reference" && att.url.startsWith("DocumentReference/")) {
                        if (!resolvedDocumentReferences[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching DocumentReference:", att.url);
                                //const docRef = await fhirClient.request(att.url);
                                const docRef = await fetchDocumentReferenceByReference(att.url, fhirClient, store);
                                setResolvedDocumentReferences(prev => ({ ...prev, [att.url]: docRef }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch DocumentReference:", att.url, err);
                            }
                        }
                        continue;
                    }

                    //----Media---------
                    // Need to figure out how to handle attachments on cached resources. Add forced flag to fetch so it doesn't get cached version.
                    if (att.type === "reference" && att.url.startsWith("Media/")) {
                        if (!resolvedMediaReferences[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching MediaReference:", att.url);
                                const ref = await fetchMediaByReference(att.url, fhirClient, store);
                                setResolvedMediaReferences(prev => ({ ...prev, [att.url]: ref }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch MediaReference:", att.url, err);
                            }
                        }
                        continue;
                    }

                    //Specimens
                    if (att.type === "reference" && att.url.startsWith("Specimen/")) {
                        if (!resolvedSpecimenReferences[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching SpecimenReference:", att.url);
                                const ref = await fetchSpecimenByReference(att.url, fhirClient, store);
                                setResolvedSpecimenReferences(prev => ({ ...prev, [att.url]: ref }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch SpecimenReference:", att.url, err);
                            }
                        }
                        continue;
                    }


                     //CarePlans
                    if (att.type === "reference" && att.url.startsWith("CarePlan/")) {
                        if (!resolvedCarePlanReferences[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching CarePlanReference:", att.url);
                                const ref = await fetchCarePlanByReference(att.url, fhirClient, store);
                                setResolvedCarePlanReferences(prev => ({ ...prev, [att.url]: ref }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch CarePlanReference:", att.url, err);
                            }
                        }
                        continue;
                    }


                       //Conditions
                    if (att.type === "reference" && att.url.startsWith("Condition/")) {
                        if (!resolvedConditionReferences[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching ConditionReference:", att.url);
                                const ref = await fetchConditionByReference(att.url, fhirClient, store);
                                setResolvedConditionReferences(prev => ({ ...prev, [att.url]: ref }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch ConditionReference:", att.url, err);
                            }
                        }
                        continue;
                    }

                        //QuestionnaireResponse
                    if (att.type === "reference" && att.url.startsWith("QuestionnaireResponse/")) {
                        if (!resolvedQuestionnaireResponseReferences[att.url]) {
                            try {
                                console.log("[ThreadMessageViewer] Fetching QuestionnaireResponseReference:", att.url);
                                const ref = await fetchQuestionnaireResponseByReference(att.url, fhirClient, store);
                                setResolvedQuestionnaireResponseReferences(prev => ({ ...prev, [att.url]: ref }));
                            } catch (err) {
                                console.error("[ThreadMessageViewer] Failed to fetch QuestionnaireResponseReference:", att.url, err);
                            }
                        }
                        continue;
                    }



                }
            }
        }

        if (messages.length > 0) {
            fetchReferencedResources();
        }
    }, [messages, fhirClient, resolvedObservations, resolvedDiagnosticReports, resolvedDocumentReferences]);

    return (
        <div>
            <Card className="p-2 border border-dark bg-body-secondary">
                <Card.Header className={`d-flex justify-content-between align-items-center mb-0 ${styles.conversationHeader}`}>
                    <h5 className="mb-0">
                        Participants:&nbsp;
                        {threadTitle.split(", ").map((nameOrRef, idx, arr) => {
                            let provider = null;
                            let providerId = null;

                            if (nameOrRef.startsWith("Practitioner/")) {
                                providerId = nameOrRef.split("/")[1];
                                provider = providers[providerId];
                            } else {
                                provider = Object.values(providers).find(p => {
                                    const fullName =
                                        (p.name?.[0]?.text ||
                                            `${p.name?.[0]?.given?.join(" ")} ${p.name?.[0]?.family}`)?.trim();
                                    return fullName === nameOrRef;
                                });
                                providerId = provider?.id;
                            }

                            if (!provider && contextProvider) {
                                const contextFullName =
                                    (contextProvider.name?.[0]?.text ||
                                        `${contextProvider.name?.[0]?.given?.join(" ")} ${contextProvider.name?.[0]?.family}`)?.trim();
                                if (
                                    nameOrRef === contextFullName ||
                                    nameOrRef === `Practitioner/${contextProvider.id}`
                                ) {
                                    provider = contextProvider;
                                    providerId = contextProvider.id;
                                }
                            }

                            const gender = provider?.gender;
                            const iconSrc = gender === "female" ? DoctorFemaleIcon : DoctorMaleIcon;

                            return (
                                <span key={idx} className="d-inline-flex align-items-center me-2">
                                    {provider ? (
                                        <span
                                            onClick={() => {
                                                setProviderModalDetails(provider);
                                                setShowProviderModal(true);
                                            }}
                                            className={styles.iconWrapper}
                                            title={`View ${nameOrRef} details`}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <img
                                                src={iconSrc}
                                                alt="Doctor Icon"
                                                className={styles.genderIcon}
                                                style={{ marginRight: "4px" }}
                                            />
                                        </span>
                                    ) : null}
                                    <span>{nameOrRef}</span>
                                    {idx < arr.length - 1 && <span>,&nbsp;</span>}
                                </span>
                            );
                        })}
                    </h5>
                </Card.Header>

                {fetchedEncounter && typeof fetchedEncounter === "object" && "error" in fetchedEncounter && (
                    <>
                        {fetchedEncounter.error === 401 || fetchedEncounter.error === 410 ? (
                            <div className="p-3 text-center text-danger fw-bold border-top border-dark bg-body">
                                Encounter removed. Please start a new conversation.
                            </div>
                        ) : (
                            <div className="p-3 text-center text-danger fw-bold border-top border-dark bg-body">
                                Error loading encounter (code: {fetchedEncounter.error}).
                            </div>
                        )}
                    </>
                )}

                {(!fetchedEncounter?.error) && (
                    <QuickResponse
                        selectedComm={selectedComm}
                        fetchedProvider={fetchedProvider}
                        fhirClient={fhirClient}
                        contextProvider={contextProvider}
                        communicationService={communicationService}
                        fetchedPatient={fetchedPatient}
                        fetchedEncounter={fetchedEncounter}
                        setShowThanksModal={setShowThanksModal}
                        setShowReplyModal={setShowReplyModal}
                        onSendLocalReply={(msg) => {
                            setSelectedComm(prev => ({
                                ...prev,
                                injectedMessages: [...(prev?.injectedMessages || []), msg],
                            }));
                        }}
                        store={store}
                    />
                )}

                {loadingNames && (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                        <Spinner animation="border" role="status" />
                        <span className="ms-2">Resolving names...</span>
                    </div>
                )}

                {!loadingNames && messages.length === 0 && <p>Select a message to load conversation.</p>}

                {!loadingNames && messages.map(msg => {
                    const senderRef = msg.sender?.reference || "Unknown Sender";
                    const sender = nameMap[senderRef] || senderRef;
                    const content = msg.payload?.[0]?.contentString || "[No content]";

                    const readReceipts = (msg.extension || [])
                        .map(ext => {
                            const userEntry = ext.extension?.find(e => e.url === "user");
                            const readAtEntry = ext.extension?.find(e => e.url === "readAt");
                            const reference = userEntry?.valueReference?.reference;
                            const readAt = readAtEntry?.valueDateTime;
                            return reference ? { reference, readAt } : null;
                        })
                        .filter(Boolean)
                        .filter(receipt => receipt.reference !== senderRef);

                    const attachments = getMessageAttachments(msg);
                    //console.log("[ThreadMessageViewer] Attachments found:", attachments);

                    return (
                        <Card
                            key={msg.id}
                            className={`mb-3 shadow-sm ${isSelfMessage(msg, contextProvider.id) ? styles.selfMessage : styles.receivedMessage} ${msg.id === selectedComm?.id ? styles.selectedCard : ""}`}
                        >
                            <Card.Body>
                                <Card.Title className={styles.customTitle}>
                                    {sender}
                                    {msg.sent && (
                                        <span className="text-muted ms-2">
                                            {new Date(msg.sent).toLocaleString()}
                                        </span>
                                    )}
                                </Card.Title>

                                <Card.Text className={styles.messageContent}>{content}</Card.Text>

                                {attachments.length > 0 && (
                                    <div className="mt-2">
                                        <div className="fw-bold small">Attachments:</div>
                                        <ul className="list-unstyled mb-0">
                                            {attachments.map((att, i) => {

                                                // --- 1. Observation (resolved) ---
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("Observation/") &&
                                                    resolvedObservations[att.url]
                                                ) {
                                                    const observation = resolvedObservations[att.url];

                                                    //TODO: use displayName function
                                                    const title =
                                                        observation.code?.text ||
                                                        observation.code?.coding?.[0]?.display ||
                                                        observation.id ||
                                                        "Observation";

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <ObservationCard
                                                                observationData={observation}
                                                                depth={0}
                                                                title={title}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }

                                                // --- 2. DiagnosticReport (resolved) ---
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("DiagnosticReport/") &&
                                                    resolvedDiagnosticReports[att.url]
                                                ) {
                                                    const report = resolvedDiagnosticReports[att.url];
                                                    const title =
                                                        report.code?.text ||
                                                        report.code?.coding?.[0]?.display ||
                                                        report.id ||
                                                        "Diagnostic Report";

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <DiagnosticCard
                                                                reportData={report}
                                                                depth={0}
                                                                title={title}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }

                                                // --- 3. DocumentReference (resolved) ---
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("DocumentReference/") &&
                                                    resolvedDocumentReferences[att.url]
                                                ) {
                                                    const docRef = resolvedDocumentReferences[att.url];
                                                    const title =
                                                        docRef?.description ||
                                                        docRef?.content?.[0]?.attachment?.title ||
                                                        docRef.id ||
                                                        "Document Reference";

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <DocumentReferenceCard
                                                                documentData={docRef}
                                                                depth={0}
                                                                title={title}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }

                                                //Media
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("Media/") &&
                                                    resolvedMediaReferences[att.url]
                                                ) {
                                                    const ref = resolvedMediaReferences[att.url];

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <MediaCard
                                                                mediaData={ref}
                                                                depth={0}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }

                                                //Specimen
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("Specimen/") &&
                                                    resolvedSpecimenReferences[att.url]
                                                ) {
                                                    const specimenData = resolvedSpecimenReferences[att.url];

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <SpecimenCard
                                                                specimenData={specimenData}
                                                                depth={0}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }


                                                //CarePlan
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("CarePlan/") &&
                                                    resolvedCarePlanReferences[att.url]
                                                ) {
                                                    const carePlanData = resolvedCarePlanReferences[att.url];

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <CarePlanCard
                                                                carePlanData={carePlanData}
                                                                depth={0}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }


                                                  //Condition
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("Condition/") &&
                                                    resolvedConditionReferences[att.url]
                                                ) {
                                                    const conditionData = resolvedConditionReferences[att.url];

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <ConditionCard
                                                                conditionData={conditionData}
                                                                depth={0}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }



                                                   //QuestionnaireResponse
                                                if (
                                                    att.type === "reference" &&
                                                    att.url.startsWith("QuestionnaireResponse/") &&
                                                    resolvedQuestionnaireResponseReferences[att.url]
                                                ) {
                                                    const qrData = resolvedQuestionnaireResponseReferences[att.url];
                                                    console.log("!!!!!!!!!!!!!!!!!!!!!");

                                                    return (
                                                        <li key={i} className="mb-2">
                                                            <QuestionnaireResponseCard
                                                                qrData={qrData}
                                                                depth={0}
                                                                fhirClient={fhirClient}
                                                            />
                                                        </li>
                                                    );
                                                }



                                                // --- 4. Fallback: download or JSON view ---
                                                const title = att.title || att.id || "Attachment";

                                                return (
                                                    <li key={i}>
                                                        <button
                                                            className="btn btn-link p-0"
                                                            onClick={async () => {
                                                                try {
                                                                    const result = await fetchAttachment(att, fhirClient);

                                                                    if (result.error) {
                                                                        setModalMessage(
                                                                            <div>
                                                                                ❌ Could not fetch attachment/resource.<br />
                                                                                <pre style={{
                                                                                    whiteSpace: "pre-wrap",
                                                                                    maxHeight: "400px",
                                                                                    overflowY: "auto"
                                                                                }}>
                                                                                    {result.error}
                                                                                </pre>
                                                                            </div>
                                                                        );
                                                                        setShowModal(true);
                                                                        return;
                                                                    }

                                                                    // Blob download
                                                                    if (result.blob) {
                                                                        const blobUrl = URL.createObjectURL(result.blob);
                                                                        const a = document.createElement("a");
                                                                        a.href = blobUrl;
                                                                        a.download = result.title || `Attachment-${i + 1}`;
                                                                        a.click();
                                                                        return;
                                                                    }

                                                                    // Resource JSON viewer
                                                                    setModalMessage(
                                                                        <pre style={{
                                                                            whiteSpace: "pre-wrap",
                                                                            maxHeight: "400px",
                                                                            overflowY: "auto"
                                                                        }}>
                                                                            {JSON.stringify(result.resource, null, 2)}
                                                                        </pre>
                                                                    );
                                                                    setShowModal(true);

                                                                } catch (err) {
                                                                    setModalMessage(
                                                                        <div>
                                                                            ❌ Failed to fetch attachment/resource.<br />
                                                                            <pre style={{
                                                                                whiteSpace: "pre-wrap",
                                                                                maxHeight: "400px",
                                                                                overflowY: "auto"
                                                                            }}>
                                                                                {err.message}
                                                                            </pre>
                                                                        </div>
                                                                    );
                                                                    setShowModal(true);
                                                                }
                                                            }}
                                                        >
                                                            {getIconForContentType(att.contentType, "CommPayload")}
                                                            {title} ({att.contentType || "N/A"})
                                                        </button>

                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                                {readReceipts.length > 0 && (() => {
                                    const validEntries = readReceipts.filter(entry => entry.readAt);
                                    const seenNames = new Set();
                                    const uniqueEntries = validEntries.filter(entry => {
                                        const name = nameMap[entry.reference] || entry.reference;
                                        if (!name || seenNames.has(name)) return false;
                                        seenNames.add(name);
                                        return true;
                                    });

                                    return uniqueEntries.length > 0 && (
                                        <div>
                                            <p></p>
                                            Read by:
                                            <ul style={{ paddingLeft: "1rem", marginTop: "0.5rem", listStyleType: "none" }}>
                                                {uniqueEntries.map((entry, index) => {
                                                    const name = nameMap[entry.reference] || entry.reference;
                                                    return (
                                                        <li key={index}>
                                                            👁️ {name}
                                                            <span className="ms-2 text-muted">
                                                                on {new Date(entry.readAt).toLocaleString()}
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    );
                                })()}
                            </Card.Body>
                        </Card>
                    );
                })}
            </Card>

            <ResourceViewerModal
                show={showProviderModal}
                onClose={handleProviderModalClose}
                resource={providerModalDetails}
            />

            <AttachmentErrorModal
                show={showModal}
                onClose={() => setShowModal(false)}
                message={modalMessage}
            />
        </div>
    );
}

export default ThreadMessageViewer;
